import {createContext, useCallback, useContext, useEffect, useState} from "react";
import {
  DEFAULT_EUR_RATE,
  getCurrency,
  getLang,
  setCurrency as apiSetCurrency,
  setLang as apiSetLang,
  type Currency,
  type Lang,
  translate,
  type I18nKey
} from "@corporatesclub/shared";

const CURRENCY_KEY = "cc-currency";

type I18nCtx = {
  lang: Lang;
  currency: Currency;
  eurRate: number;
  t: (key: I18nKey) => string;
  setLang: (lang: Lang) => Promise<void>;
  setCurrency: (currency: Currency) => Promise<void>;
};

const Ctx = createContext<I18nCtx>({
  lang: "en",
  currency: "ron",
  eurRate: DEFAULT_EUR_RATE,
  t: (k) => translate("en", k),
  setLang: async() => {},
  setCurrency: async() => {}
});

function readStoredCurrency(): Currency | null {
  if(typeof window === "undefined") return null;
  const v = localStorage.getItem(CURRENCY_KEY);
  return v === "eur" || v === "ron" ? v : null;
}

export function I18nProvider({children}: {children: React.ReactNode}) {
  const [lang, setLangState] = useState<Lang>("en");
  const [currency, setCurrencyState] = useState<Currency>(() => readStoredCurrency() || "ron");
  const [eurRate, setEurRate] = useState(DEFAULT_EUR_RATE);
  useEffect(() => {
    getLang().then((r) => setLangState(r.lang)).catch(() => setLangState("en"));
    getCurrency().then((r) => {
      setCurrencyState(r.currency);
      if(r.eurRate && r.eurRate > 0) setEurRate(r.eurRate);
      localStorage.setItem(CURRENCY_KEY, r.currency);
    }).catch(() => {});
  }, []);
  const t = useCallback((key: I18nKey) => translate(lang, key), [lang]);
  const setLang = useCallback(async(l: Lang) => {
    await apiSetLang(l);
    setLangState(l);
  }, []);
  const setCurrency = useCallback(async(c: Currency) => {
    const r = await apiSetCurrency(c);
    setCurrencyState(r.currency);
    if(r.eurRate && r.eurRate > 0) setEurRate(r.eurRate);
    localStorage.setItem(CURRENCY_KEY, r.currency);
  }, []);
  return <Ctx.Provider value={{lang, currency, eurRate, t, setLang, setCurrency}}>{children}</Ctx.Provider>;
}

export function useI18n() {
  return useContext(Ctx);
}
